/**
 * `<caos-listing-card>` — one marketplace listing as a card.
 * (Catalog: Data display › ListingCard — one Exchange listing as a card: publisher, title, tagline, type, price.)
 *
 * The card for the CAOS Exchange: a publisher mark and name, the listing title and tagline, the
 * package-type and pricing chips, and a review pill. The whole card is one link to the listing's
 * detail page, so it is keyboard-reachable by default rather than by arrangement.
 *
 * ── WHY THIS IS NOT A `CardGrid` CARD ────────────────────────────────────────
 * `CardGrid` draws a RECORD SET four-ways-compatible with `DataTable`, `KanbanBoard` and
 * `SplitView`, and its card is deliberately compact: a name, a sub-line, and up to two rollup
 * metrics. That restraint is the Accounts gold standard, and it exists to stop the earlier Contacts
 * card that dumped every field. A marketplace listing is a different object: the publisher identity,
 * the type, the price and the review state are not decoration on a record's name, they are what a
 * visitor chooses between. So this is its own piece rather than a sixth `ListMode`, and a Listing
 * list view in `card` mode still renders through `CardGrid` unchanged.
 *
 * ── IT COMPOSES `Tag` AND `StatusPill` RATHER THAN DRAWING CHIPS ─────────────
 * The type and price chips are `<caos-tag>` — "a free-form operational label, distinct from
 * lifecycle state" — and the review mark is `<caos-status-pill intent="success">`. Neither is
 * redrawn here. A sixth bespoke chip style is the exact failure the reference's `aliases` field
 * exists to prevent: five people building the same thing five ways because nobody found the one
 * that shipped. Both are side-effect imported below, so a page that loads only this card still gets
 * them defined.
 *
 * ── THE REVIEW PILL READS ONE FIELD, AND IT IS NOT THE OBVIOUS ONE ───────────
 * This card renders from `listing.review_badge` and MUST NEVER read
 * `listing.security_review_status`. That is a confidentiality property, not a styling preference.
 * `security_review_status` carries `classification: sensitive` and says of itself: "Operator only.
 * Not readable by a publisher and never by a visitor." `review_badge` exists precisely to be the
 * public projection of it — "true only when it is `passed`. Boolean deliberately — it cannot leak
 * the difference between pending and flagged." A card that read the Select would leak an operator's
 * verdict-in-progress to the public web; a card that reads the Boolean cannot, whatever it does with
 * it. Hence one attribute, `reviewed`, and no way to express anything finer.
 *
 * ── WHAT THIS CARD DELIBERATELY DOES NOT DRAW ────────────────────────────────
 * No "Verified" badge. The Exchange's Publisher object has exactly `bio`, `company_name`,
 * `logo_url`, `publisher_org_id`, `slug` and `support_url` — there is no verification field, so
 * there is no verification the platform performs. Drawing a Verified badge beside the Reviewed one
 * would assert two independent checks where there is one.
 *
 * No rating row and no install row. `listing.rating_avg` and `listing.install_count` are real
 * stored columns, and both are EMPTY IN PRODUCTION for every listing, permanently, until a writer
 * exists — `install_count` says "NO WRITER EXISTS YET ... a stored column that nothing currently
 * fills", and `rating_avg` names CWEB-151 as the ticket that would fill it. A rating slot blank on
 * every card does not read as "the platform has no ratings"; it reads as "this listing has none",
 * which is a worse untruth than the zeros it replaced. The `rating_avg` and `install_count` INPUTS
 * are declared in `listing-card.docs.ts` all the same, so CWEB-151 can switch them on without a
 * manifest change: an input name is the key a stored placement writes its value under, so the input
 * contract freezes the moment a component becomes placeable, and adding one afterwards is the
 * expensive migration. Declaring two that render nothing costs a line each.
 *
 * ── THE IMAGE IS THE PUBLISHER'S MARK, NOT LISTING ARTWORK ───────────────────
 * `publisher-logo` is the only image the Exchange has: Listing carries no media, screenshot,
 * gallery or hero column. So the media area shows the PUBLISHER's logo and is labelled as such. It
 * is drawn once — rendering the same asset twice, as a "thumbnail" and again beside the publisher
 * name, would be padding dressed as two facts. With no logo the area falls back to the `package`
 * glyph rather than a broken image.
 *
 * ── TOKENS: EMBER IS `heat`, NOT `accent` ────────────────────────────────────
 * Hover and focus warm to `--caos-color-heat`, the ember of "carbon ground / ember accent". This is
 * deliberate and worth stating, because the reflex is `--caos-color-accent` and that would be wrong
 * twice. Under the Dragon brand `--caos-color-accent` resolves through `--brand-accent` to
 * `#C91313`, Dragon's RED — not its ember; ember is the second accent, and the design system names
 * it `--caos-color-heat`. And `heat` is a core token, defined by `caos-default.css` as well as
 * `dragon.css`, so reaching for it is not brand-coupling: a brand that overrides heat moves this
 * card with it. Neither token carries TEXT here — `dragon.css` records that `--caos-color-accent`
 * measures 3.60:1, under the 4.5:1 AA floor — so the warm tokens only ever draw a border or a ring.
 *
 * The title's `15px` is a literal because the type scale has no step for it: it runs
 * `--caos-text-body` 14px then `--caos-text-kpi` 26px then `--caos-text-h2` 28px, and a card title
 * is none of those. `CardGrid`'s card name is `15px` for the same reason, so the two cards sit at
 * one size rather than at two invented tokens.
 *
 * ── `listing-title`, NOT `title` ─────────────────────────────────────────────
 * The listing's name arrives as `listing-title`. `title` is a global HTML attribute: observing it
 * would give every card a browser tooltip of its own heading, appearing on hover over the whole
 * card and read out twice by a screen reader.
 *
 * Styling seams: `::part(card)`, `::part(media)`, `::part(publisher)`, `::part(title)`,
 * `::part(tagline)`, `::part(chips)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { below } from '../breakpoints.js';
import { makeIcon } from './icons.js';
// Side-effect imports: this card composes both, so a page that loads only this module still has
// them defined rather than rendering two unknown elements.
import './tag.js';
import './status-pill.js';

const sheet = makeSheet(`
  :host { display: block; }

  .card {
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-3);
    padding: var(--caos-space-4);
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-lg);
    min-width: 0;
    height: 100%;
    box-sizing: border-box;
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    transition: border-color 0.12s ease, box-shadow 0.12s ease;
  }

  /* Ember on hover and focus. The ring is on :focus-visible, so a pointer click does not leave one.
     The outline is TRANSPARENT rather than none: Windows high-contrast / forced-colors mode drops
     box-shadow entirely, so a ring drawn only as a shadow leaves that user with no focus indicator
     at all. A transparent outline is invisible normally and is forced to a system colour there. */
  .card:hover { border-color: var(--caos-color-heat); }
  .card:focus-visible {
    outline: 2px solid transparent;
    outline-offset: 2px;
    border-color: var(--caos-color-heat);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--caos-color-heat) 28%, transparent);
  }

  /* media — the publisher's mark, on the carbon ground. */
  .media {
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 16 / 9;
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-bg);
    border: 1px solid var(--caos-color-border);
    overflow: hidden;
  }
  .media img { max-width: 60%; max-height: 60%; object-fit: contain; }
  .media svg { width: 28px; height: 28px; color: var(--caos-color-text-muted); }

  .publisher {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    font-family: var(--caos-font-mono);
    font-size: var(--caos-text-label);
    letter-spacing: var(--caos-tracking-wide);
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
    min-width: 0;
  }
  .publisher-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  /* Hidden by attribute, not by :empty. The name always sits in a child span, so this row is never
     childless and :empty would never have matched it — it would have left an empty row and its gap on
     every card with no publisher. The attribute is set in sync(); this rule is needed because the
     display: flex above would otherwise beat the UA stylesheet's rule for [hidden]. */
  .publisher[hidden] { display: none; }

  .title {
    font-family: var(--caos-font-display);
    font-weight: 700;
    font-size: 15px; /* no scale step between body 14px and kpi 26px; CardGrid's card name is 15px */
    line-height: 1.15;
    color: var(--caos-color-text);
    overflow-wrap: anywhere;
  }
  .title:empty { display: none; }

  .tagline {
    font-size: var(--caos-text-body);
    line-height: 1.4;
    color: var(--caos-color-text-muted);
    /* Two lines, so one long tagline cannot change a card's height in a grid of them. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .tagline:empty { display: none; }

  /* chips — pushed to the bottom so a row of cards aligns on them whatever the tagline's length. */
  .chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--caos-space-2);
    margin-top: auto;
  }
  .chips:empty { display: none; }

  /* Below the small step the card is full-width, so a 16:9 mark eats the viewport; flatten it.
     The width comes from the breakpoint axis rather than a literal — a custom property cannot be
     read inside a media query, so the scale lives in breakpoints.ts and is interpolated here. */
  @media ${below('small')} {
    .media { aspect-ratio: 3 / 1; }
  }
`);

/**
 * The values of `reviewed` that mean NOT reviewed. Compared after trim + lowercase, so each entry
 * covers its own casing and surrounding whitespace. Anything else present means reviewed.
 */
const REVIEWED_OFF = new Set(['false', '0', 'no', 'off']);

/**
 * The card's `href` as something safe to put on an anchor, or `null`.
 *
 * THIS GUARD EXISTS BECAUSE OF THE NAVIGATION FIX ABOVE, and that is the whole reason it is here
 * rather than in whatever writes the value. While every unmodified click was being `preventDefault`ed
 * the browser never resolved this attribute on an ordinary click, so a `javascript:` URL arriving
 * through stored metadata was inert on that path. Letting the link navigate when nothing routes it —
 * which is what makes the card work standalone — is also what makes that URL execute. The fix that
 * removed one defect handed a second one its reachability, so it ships with its own guard.
 *
 * TWO conditions, and the second was missing from the first version of this guard. A scheme check
 * alone stops a URL that EXECUTES and lets through one that merely leaves: resolved against an https
 * base, `//evil.example.com/x` comes back as `https:` and passes, and so does the backslash form
 * `\\evil.example.com`. Both are "relative references" by the URL spec, so a guard phrased as
 * "http, https or relative" admits them while reading as though it did not. On this card that is an
 * open redirect on the one surface whose entire job is telling a visitor where a package comes from.
 *
 * So the href must ALSO resolve to this document's own origin. That is what the ticket actually asks
 * for — the route is `/listing/:publisher/:slug`, a path on the workspace — and it matches the
 * platform's own stance elsewhere: a component's bundle is "resolved against the workspace's own
 * origin, never elsewhere". Admitting an off-site absolute URL was an invention of this guard's first
 * draft, not a requirement of anything.
 *
 * A relative route is navigable. Everything else — another origin, `javascript:`, `data:`,
 * `vbscript:`, `file:`, `mailto:` — resolves to nothing and the card draws without an href, which the
 * component already handles as "draws but does not navigate".
 */
function navigableHref(raw: string): string | null {
  const value = raw.trim();
  if (value === '') return null;
  try {
    // The DOCUMENT is the base, not a placeholder: the question is whether this lands on the same
    // origin as the page, which a synthetic base cannot answer.
    const here = window.location;
    // THE DOCUMENT'S OWN ORIGIN HAS TO BE A REAL ONE FIRST, and this clause is the reason the other
    // two are safe. An opaque origin — a file: document, a sandboxed iframe — stringifies as "null",
    // and a javascript: or data: URL's origin is ALSO "null", so on such a page the same-origin
    // comparison below would compare "null" with "null", succeed, and admit exactly what it exists to
    // refuse. Mutating away the url-protocol check alone leaves every test here green, because with an
    // http(s) document the origin check already implies it; that redundancy is only true WHILE this
    // clause holds. Stated rather than left as a silent dependency: no test below distinguishes the
    // url-protocol clause, because the harness always serves an http origin.
    if (here.protocol !== 'http:' && here.protocol !== 'https:') return null;
    const url = new URL(value, here.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.origin !== here.origin) return null;
    return value;
  } catch {
    return null;
  }
}

export class CaosListingCard extends CaosElement {
  static get observedAttributes(): string[] {
    return [
      'listing-title',
      'tagline',
      'publisher-name',
      'publisher-logo',
      'package-type',
      'pricing-label',
      'reviewed',
      'href',
    ];
  }

  private cardEl: HTMLAnchorElement;
  private mediaEl: HTMLDivElement;
  private publisherEl: HTMLDivElement;
  private publisherNameEl: HTMLSpanElement;
  private titleEl: HTMLDivElement;
  private taglineEl: HTMLDivElement;
  private chipsEl: HTMLDivElement;
  /** The `publisher-logo` the media area was last built from. `undefined` until it is built once. */
  private renderedLogo: string | null | undefined = undefined;

  constructor() {
    super(sheet);

    // An anchor, not a div with a role. A real href is focusable, announced as a link, and supports
    // what a visitor expects of one — middle-click, open in a new tab, copy address — none of which
    // a keydown handler on a div gives back.
    const card = document.createElement('a');
    card.className = 'card';
    card.setAttribute('part', 'card');
    this.cardEl = card;

    // Navigation offers a route and FALLS BACK TO THE LINK. The event is `cancelable`, and the
    // browser's own navigation is suppressed ONLY when a listener cancels it — i.e. only when
    // something is actually routing. An unconditional `preventDefault()` here would mean a card
    // placed on a page with no router did nothing at all when clicked, which is worse than a full
    // page load: the href would be a decoration, and "the card navigates to the listing" would be
    // false for the standalone case. So the host opts IN to intercepting:
    //
    //   card.addEventListener('caos-listing-select', (e) => { e.preventDefault(); router.go(e.detail.href); });
    //
    // A modified click is never touched, so open-in-new-tab, copy-address and middle-click keep
    // working — and those must reach the real href, which is why nothing is dispatched for them.
    card.addEventListener('click', (e) => {
      // The SAME guard the rendered href goes through, not the raw attribute: a value this card
      // refused to put on the anchor must not reach a host router either, or the guard would only
      // have moved the hole rather than closed it.
      const raw = this.getAttribute('href');
      const href = raw === null ? null : navigableHref(raw);
      if (!href || e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const routed = !this.dispatchEvent(
        new CustomEvent('caos-listing-select', {
          bubbles: true,
          composed: true,
          cancelable: true,
          detail: { href, title: this.getAttribute('listing-title') ?? '' },
        }),
      );
      if (routed) e.preventDefault();
    });

    this.mediaEl = document.createElement('div');
    this.mediaEl.className = 'media';
    this.mediaEl.setAttribute('part', 'media');

    this.publisherEl = document.createElement('div');
    this.publisherEl.className = 'publisher';
    this.publisherEl.setAttribute('part', 'publisher');
    this.publisherNameEl = document.createElement('span');
    this.publisherNameEl.className = 'publisher-name';
    this.publisherEl.append(this.publisherNameEl);

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');

    this.taglineEl = document.createElement('div');
    this.taglineEl.className = 'tagline';
    this.taglineEl.setAttribute('part', 'tagline');

    this.chipsEl = document.createElement('div');
    this.chipsEl.className = 'chips';
    this.chipsEl.setAttribute('part', 'chips');

    card.append(this.mediaEl, this.publisherEl, this.titleEl, this.taglineEl, this.chipsEl);
    this.root.append(card);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /** A `<caos-tag>` carrying one label in its default slot. */
  private tag(text: string): HTMLElement {
    const el = document.createElement('caos-tag');
    el.textContent = text;
    return el;
  }

  private sync(): void {
    const title = this.getAttribute('listing-title') ?? '';
    const publisherName = this.getAttribute('publisher-name') ?? '';
    this.titleEl.textContent = title;
    this.taglineEl.textContent = this.getAttribute('tagline') ?? '';
    this.publisherNameEl.textContent = publisherName;

    // The publisher row holds a span whether or not there is a name in it, so it is never `:empty`;
    // hide it explicitly or an unattributed listing carries an empty row and its gap.
    this.publisherEl.toggleAttribute('hidden', publisherName === '');

    const rawHref = this.getAttribute('href');
    const href = rawHref === null ? null : navigableHref(rawHref);
    if (href) this.cardEl.href = href;
    else this.cardEl.removeAttribute('href');

    // NO `aria-label` ON THE CARD. It was here, set to the title, to stop the anchor being announced
    // as the concatenation of everything inside it. That was the wrong trade: `aria-label` REPLACES
    // the accessible name outright, so it does not shorten the announcement, it DELETES the rest of
    // it — the package type, the price, "Reviewed", the tagline and the publisher all stop being
    // readable as part of the link. Those five are exactly what a visitor is choosing between, which
    // is the whole argument in the header for this being its own component rather than a CardGrid
    // card. A card link whose name is its content is the normal pattern and the longer announcement
    // is the correct one. (It was also inert on a card with no href: with no href the element is not
    // a link, and no accessible name is computed for it at all.)

    // media — the publisher's mark, or the package glyph when there is none. Never a broken image.
    // Rebuilt only when the logo actually changes: any observed attribute re-runs sync(), and
    // recreating the <img> each time re-enters it into lazy-loading from scratch, so a card that has
    // its price or review state set after mount would blink its logo for no reason.
    const logo = this.getAttribute('publisher-logo');
    if (logo !== this.renderedLogo) {
      this.renderedLogo = logo;
      if (logo) {
        const img = document.createElement('img');
        img.src = logo;
        // Decorative: the publisher's name is already text in the card, so naming it here would have
        // a screen reader read the publisher twice.
        img.alt = '';
        img.loading = 'lazy';
        this.mediaEl.replaceChildren(img);
      } else {
        const glyph = makeIcon('package');
        glyph.setAttribute('aria-hidden', 'true');
        this.mediaEl.replaceChildren(glyph);
      }
    }

    // chips — type, then price, then the review pill. No rating and no install count: both fields
    // exist and nothing writes them, so a slot for either would be blank on every card forever.
    this.chipsEl.replaceChildren();
    const type = this.getAttribute('package-type');
    if (type) this.chipsEl.append(this.tag(type));
    const price = this.getAttribute('pricing-label');
    if (price) this.chipsEl.append(this.tag(price));

    // `reviewed` is the public Boolean projection and the only review fact this card can see.
    //
    // THE OFF SET IS A SET, not the one spelling. A first version of this honoured exactly the string
    // "false", which is the shape of the problem rather than its extent: the argument for deviating
    // from HTML's presence-only boolean is that a value arrives from a SERIALISER, and a serialiser
    // that writes "false" is no likelier than one writing "False", "FALSE" or "0". Covering one and
    // claiming the reasoning was the same error as writing a one-element loop to prove it.
    //
    // `"false"` COUNTS AS FALSE, which is not how an HTML boolean attribute normally behaves — for
    // `disabled` or `hidden` the value is ignored and presence alone is the signal. The deviation is
    // deliberate and it is specific to what this pill asserts. The input is declared `type: 'boolean'`
    // with `default: false`, and a placement's stored value arrives through metadata as a STRING; a
    // serialiser that writes `reviewed="false"` for a false boolean is an ordinary thing to meet, and
    // under presence-only semantics that renders an UNREVIEWED listing with a green "Reviewed" pill.
    // Every other mark on this card is a claim about itself, so a wrong one is cosmetic. This one is
    // a claim that the operator reviewed the package, shown to a visitor deciding whether to trust
    // it, and the failure is silent and in the unsafe direction. So the explicit false is honoured.
    const reviewed = this.getAttribute('reviewed');
    if (reviewed !== null && !REVIEWED_OFF.has(reviewed.trim().toLowerCase())) {
      const pill = document.createElement('caos-status-pill');
      pill.setAttribute('intent', 'success');
      pill.setAttribute('dot', '');
      pill.textContent = 'Reviewed';
      this.chipsEl.append(pill);
    }
  }
}

define('caos-listing-card', CaosListingCard);

declare global {
  interface HTMLElementTagNameMap {
    'caos-listing-card': CaosListingCard;
  }
}
